import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  ShoppingBagIcon,
  CheckCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts'
import analyticsApiService from '../../services/analyticsApiService'
import useAuthStore from '../../stores/useAuthStore'

const AnalyticsTab = () => {
  const { profile } = useAuthStore()
  const [timeRange, setTimeRange] = useState('last_7_days')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [analyticsData, setAnalyticsData] = useState(null)

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!profile?.restaurant_id) {
        console.log('No restaurant ID available');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching analytics for restaurant:', profile.restaurant_id, 'period:', timeRange);
        
        const response = await analyticsApiService.getAnalyticsDashboard(
          profile.restaurant_id,
          timeRange
        );

        console.log('Analytics data received:', response);

        if (response.success) {
          setAnalyticsData(response.data);
        } else {
          setError(response.message || 'Failed to fetch analytics data');
        }
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError('Failed to load analytics data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [profile?.restaurant_id, timeRange]);



  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-neutral-200">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-neutral-200 rounded w-1/3"></div>
            <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-neutral-200">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-neutral-200 rounded w-2/3"></div>
                <div className="h-8 bg-neutral-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-neutral-200">
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Extract data from analytics response
  const metrics = analyticsData?.metrics || {};
  const charts = analyticsData?.charts || {};

  return (
      <div className="space-y-12">
      {/* Premium Analytics Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10">
        <div>
          <h2 className="text-4xl font-black text-gray-900 tracking-tighter lowercase mb-4">
             <span className="text-orange-500 italic">/</span>sales reports
          </h2>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            Data Overview / {timeRange.replace(/_/g, ' ')} summary
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="relative">
            <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="pl-12 pr-10 py-4 bg-gray-50 text-gray-900 text-[10px] font-black uppercase tracking-widest rounded-2xl border border-gray-100 focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none appearance-none cursor-pointer"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last_7_days">Last 7 Days</option>
              <option value="last_30_days">Last 30 Days</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
            </select>
          </div>
        </div>
      </div>

      {/* Primary Intelligence Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {[
          { label: 'Total Revenue', value: `₹${metrics.total_revenue?.value?.toLocaleString() || 0}`, icon: CurrencyRupeeIcon, color: 'text-emerald-500', trend: metrics.total_revenue?.change_percentage, direction: metrics.total_revenue?.change_direction },
          { label: 'Orders Count', value: metrics.total_orders?.value || 0, icon: ShoppingBagIcon, color: 'text-gray-900', trend: metrics.total_orders?.change, direction: metrics.total_orders?.change_direction },
          { label: 'Avg. Bill Amt', value: `₹${metrics.avg_order_value?.value?.toFixed(0) || 0}`, icon: ChartBarIcon, color: 'text-orange-500', trend: metrics.avg_order_value?.change, direction: metrics.avg_order_value?.change_direction },
          { label: 'Revenue Growth', value: `${metrics.revenue_growth?.value?.toFixed(1) || 0}%`, icon: ArrowTrendingUpIcon, color: 'text-emerald-500', trend: null }
        ].map((metric, idx) => (
          <motion.div
            key={metric.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="bg-white rounded-[40px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 group"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100 group-hover:bg-gray-900 transition-all duration-500">
                <metric.icon className={`h-5 w-5 ${metric.color} group-hover:text-white transition-all`} />
              </div>
              {metric.trend !== null && (
                <div className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase ${metric.direction === 'up' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                  {metric.direction === 'up' ? '+' : '-'}{Math.abs(metric.trend)}%
                </div>
              )}
            </div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">{metric.label}</p>
            <h3 className="text-3xl font-black text-gray-900 tracking-tighter uppercase">{metric.value}</h3>
          </motion.div>
        ))}
      </div>

      {/* Pattern Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Temporal Sales Array */}
        <div className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-xl shadow-gray-100/50">
          <div className="flex items-center justify-between mb-10">
            <h3 className="text-xl font-black text-gray-900 tracking-tight uppercase">Hourly Sales</h3>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-orange-500"></span>
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Sales Trend</span>
            </div>
          </div>
          <div className="h-[350px]">
            {charts.sales_by_hour && charts.sales_by_hour.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.sales_by_hour}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F5A623" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#F5A623" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                  <XAxis 
                    dataKey="label" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9CA3AF', fontSize: 10, fontWeight: 900 }} 
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9CA3AF', fontSize: 10, fontWeight: 900 }} 
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)', fontWeight: 900, textTransform: 'uppercase', fontSize: '10px' }}
                    formatter={(value) => [`₹${value.toFixed(2)}`, 'Revenue']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="sales" 
                    stroke="#F5A623" 
                    strokeWidth={4}
                    fillOpacity={1} 
                    fill="url(#colorSales)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-300 gap-4">
                 <div className="w-16 h-16 bg-gray-50 rounded-3xl flex items-center justify-center border border-gray-100">
                   <ChartBarIcon className="h-8 w-8" />
                 </div>
                 <p className="text-[10px] font-black uppercase tracking-widest">No Sales Data Yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Hourly Item Insights - Peak vs Normal */}
        <div className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-xl shadow-gray-100/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-10">
            <div>
              <h3 className="text-xl font-black text-gray-900 tracking-tight uppercase">Peak Hour Menu</h3>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">
                Top items during peak: <span className="text-orange-500">{metrics.peak_hours?.display || 'N/A'}</span>
              </p>
            </div>
            <div className="flex items-center gap-3 bg-orange-50 px-4 py-2 rounded-xl border border-orange-100">
               <ClockIcon className="h-4 w-4 text-orange-500" />
               <span className="text-[10px] font-black text-orange-700 uppercase tracking-widest">Peak performance</span>
            </div>
          </div>
          
          <div className="space-y-8">
            {charts.peak_items && charts.peak_items.length > 0 ? (
              charts.peak_items.map((item, index) => (
                <div key={`peak-${item.name}`} className="flex items-center gap-6 group">
                  <div className="w-12 h-12 rounded-2xl bg-orange-500 flex items-center justify-center text-[12px] font-black text-white border border-orange-400 group-hover:scale-110 transition-all duration-300 shadow-lg shadow-orange-100">
                    #{index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-end mb-2">
                      <p className="text-[12px] font-black text-gray-900 uppercase tracking-tight">{item.name}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-orange-500 uppercase tracking-widest">{item.count} orders</span>
                      </div>
                    </div>
                    <div className="h-1.5 w-full bg-gray-50 rounded-full overflow-hidden">
                       <motion.div 
                         initial={{ width: 0 }}
                         animate={{ width: `${(item.count / charts.peak_items[0].count) * 100}%` }}
                         transition={{ duration: 1, delay: index * 0.1 }}
                         className="h-full bg-orange-500 rounded-full"
                       />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-10 text-gray-300">
                <p className="text-[10px] font-black uppercase tracking-widest">No Peak Data</p>
              </div>
            )}

            <div className="pt-8 border-t border-gray-50">
              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-6">Off-Peak/Normal Hours Top Food</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {charts.off_peak_items && charts.off_peak_items.length > 0 ? (
                  charts.off_peak_items.map((item, index) => (
                    <div key={`offpeak-${item.name}`} className="bg-gray-50 rounded-2xl p-4 border border-gray-100 hover:bg-white hover:shadow-lg transition-all duration-300 group">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">Rank 0{index + 1}</span>
                        <ShoppingBagIcon className="h-3 w-3 text-gray-300 group-hover:text-gray-900 transition-colors" />
                      </div>
                      <p className="text-[11px] font-black text-gray-900 uppercase truncate mb-1">{item.name}</p>
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{item.count} items sold</p>
                    </div>
                  ))
                ) : (
                  <p className="text-center col-span-3 text-[10px] font-black uppercase text-gray-300 py-4">No off-peak data</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Inventory Velocity */}
        <div className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-xl shadow-gray-100/50">
          <h3 className="text-xl font-black text-gray-900 tracking-tight uppercase mb-10">Popular Menu Items</h3>
          <div className="space-y-6">
            {charts.top_selling_items && charts.top_selling_items.length > 0 ? (
              charts.top_selling_items.map((item, index) => (
                <div key={item.name} className="flex items-center gap-6 group">
                  <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-[12px] font-black text-gray-900 border border-gray-100 group-hover:bg-gray-900 group-hover:text-white transition-all duration-300">
                    0{index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-end mb-2">
                      <p className="text-[12px] font-black text-gray-900 uppercase tracking-tight">{item.name}</p>
                      <p className="text-[10px] font-black text-orange-500 uppercase tracking-widest">{item.count} units</p>
                    </div>
                    <div className="h-1.5 w-full bg-gray-50 rounded-full overflow-hidden">
                       <motion.div 
                         initial={{ width: 0 }}
                         animate={{ width: `${(item.count / charts.top_selling_items[0].count) * 100}%` }}
                         transition={{ duration: 1, delay: index * 0.1 }}
                         className="h-full bg-gray-900 rounded-full"
                       />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-20 text-gray-300">
                <p className="text-[10px] font-black uppercase tracking-widest">No Sales Recorded</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Node Status Matrix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-xl shadow-gray-100/50">
          <h3 className="text-xl font-black text-gray-900 tracking-tight uppercase mb-10">Order Status Breakdown</h3>
          <div className="h-[300px]">
            {charts.orders_by_status && charts.orders_by_status.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={charts.orders_by_status}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={8}
                    dataKey="count"
                  >
                    {charts.orders_by_status.map((entry, index) => {
                      const colors = {
                        completed: '#10B981',
                        preparing: '#F59E0B',
                        ready: '#3B82F6',
                        pending: '#6B7280',
                        cancelled: '#F43F5E'
                      };
                      return <Cell key={`cell-${index}`} fill={colors[entry.status] || '#E5E7EB'} cornerRadius={10} />;
                    })}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)', fontWeight: 900, textTransform: 'uppercase', fontSize: '10px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-300">
                <p className="text-[10px] font-black uppercase tracking-widest">Status Data Unavailable</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-xl shadow-gray-100/50">
          <h3 className="text-xl font-black text-gray-900 tracking-tight uppercase mb-10">Daily Sales History</h3>
          <div className="h-[300px]">
            {charts.revenue_by_day && charts.revenue_by_day.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.revenue_by_day}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: '#9CA3AF', fontSize: 10, fontWeight: 900 }}
                    tickFormatter={(date) => {
                      const d = new Date(date);
                      return `${d.getDate()}/${d.getMonth() + 1}`;
                    }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: '#9CA3AF', fontSize: 10, fontWeight: 900 }}
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)', fontWeight: 900, textTransform: 'uppercase', fontSize: '10px' }}
                    formatter={(value) => [`₹${value.toFixed(2)}`, 'Revenue']}
                  />
                  <Bar dataKey="revenue" fill="#111827" radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-300">
                <p className="text-[10px] font-black uppercase tracking-widest">No Sales Found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsTab
